/**
 * The rate and dividend yield behind every greek, as the backend reports it
 * (strategy/market_carry.py, 2026-09-17).
 *
 * Greeks used to price at a fixed 3% (Risk page) or 4.5% (backend, screener), both with
 * no dividend. They now use the options market's own carry per expiry from ORATS, with
 * labelled fallbacks. This module turns the payload's `carry` block into the one line
 * the page shows, and says plainly when the source is a fallback or stale. A missing
 * block reads as unknown, never as the market curve.
 */

export type CarryInfo = {
  source?: "orats" | "fred" | "constant" | "request_override" | string | null;
  symbol?: string | null;
  as_of?: string | null;
  stale?: boolean | null;
  sessions_behind?: number | null;
  rate_min?: number | null;
  rate_max?: number | null;
  div_yield_min?: number | null;
  div_yield_max?: number | null;
  carry_min?: number | null;
  carry_max?: number | null;
  errors?: string[] | null;
};

/** Gamma read as the change in one contract's delta for a 1% index move
 *  (gamma x index level x 1%). Raw long-dated SPX gamma is ~0.0002 per index point, which
 *  reads as zero at any table precision -- IBKR cannot even transmit it (it sends 3 decimals).
 *  Null when either input is missing, never 0: blank is "not known", 0 would claim the line
 *  has no curvature. */
export function gammaPer1Pct(gamma: unknown, underlying: unknown): number | null {
  // Number(null) and Number("") are 0, so a missing input would scale to a confident 0.000 --
  // the exact false zero this column exists to remove. Reject the empties before coercing.
  const toNum = (v: unknown): number | null => {
    if (v === null || v === undefined || v === "") return null;
    const n = typeof v === "number" ? v : Number(v);
    return Number.isFinite(n) ? n : null;
  };
  const g = toNum(gamma);
  const s = toNum(underlying);
  if (g === null || s === null || s <= 0) return null;
  return g * s * 0.01;
}

export const GAMMA_PER_1PCT_HINT =
  "Gamma as the change in this contract's delta for a 1% move in the index " +
  "(gamma × index level × 1%). Calculated from the market's implied vol: IBKR rounds gamma " +
  "to 3 decimals, which is zero on long-dated SPX.";

export type CarrySummary = {
  /** Short text for a card subtitle. */
  label: string;
  /** Full detail for a tooltip. */
  detail: string;
  /** True when the reader should not trust the figure as current market carry. */
  warn: boolean;
};

const isNum = (v: unknown): v is number => typeof v === "number" && Number.isFinite(v);

const pct = (v: number) => `${(v * 100).toFixed(2)}%`;

const range = (lo?: number | null, hi?: number | null): string | null => {
  if (!isNum(lo) || !isNum(hi)) return null;
  return Math.abs(hi - lo) < 5e-5 ? pct(lo) : `${pct(lo)}–${pct(hi)}`;
};

export function summarizeCarry(carry: CarryInfo | null | undefined): CarrySummary {
  if (!carry || !carry.source) {
    return {
      label: "rates: unknown",
      detail: "The greeks payload did not say which rate and dividend yield it used.",
      warn: true,
    };
  }
  const rates = range(carry.rate_min, carry.rate_max);
  const divs = range(carry.div_yield_min, carry.div_yield_max);
  const nets = range(carry.carry_min, carry.carry_max);
  const numbers = [
    rates ? `rate ${rates}` : null,
    divs ? `dividend ${divs}` : null,
    nets ? `net carry ${nets}` : null,
  ]
    .filter(Boolean)
    .join(" · ");

  switch (carry.source) {
    case "orats": {
      const behind = isNum(carry.sessions_behind) ? carry.sessions_behind : null;
      const stale = carry.stale === true;
      return {
        label: stale
          ? `rates: market carry, STALE (ORATS ${carry.as_of ?? "?"}${behind !== null ? `, ${behind} sessions behind` : ""})`
          : `rates: market carry (ORATS ${carry.as_of ?? "?"})`,
        detail:
          "Each expiry is priced at the options market's own carry for that maturity: the " +
          "Treasury yield for its tenor, less the dividend yield implied by ORATS's forward. " +
          (numbers ? `${numbers}. ` : "") +
          (stale ? "The ORATS file is older than normal; carry moves slowly, but check the feed." : ""),
        warn: stale,
      };
    }
    case "fred":
      return {
        label: `rates: FALLBACK Treasuries (FRED ${carry.as_of ?? "?"}), ORATS unavailable`,
        detail:
          "ORATS could not be read, so each expiry uses the FRED Treasury yield for its tenor " +
          "with a fixed fallback dividend yield. " +
          (numbers ? `${numbers}.` : ""),
        warn: true,
      };
    case "constant":
      return {
        label: `rates: FALLBACK constant ${rates ?? "?"}, market data unavailable`,
        detail:
          "Neither ORATS nor FRED could be read, so every expiry uses one fixed rate and " +
          "dividend yield. " +
          (numbers ? `${numbers}.` : ""),
        warn: true,
      };
    case "request_override":
      return {
        label: `rate ${rates ?? "?"} (manual override, no dividend)`,
        detail: "This view was requested with an explicit ?rate=, which prices every expiry at that rate with zero dividend yield.",
        warn: true,
      };
    default:
      return {
        label: `rates: ${carry.source}`,
        detail: numbers || "Unrecognised carry source.",
        warn: true,
      };
  }
}
